"use client";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useBranch } from "@/store/branch";
import { useInstallPrompt } from "@/lib/useInstallPrompt";
import { storefrontName } from "@/lib/storefrontName";

/**
 * Branded "Add to Home Screen" banner.
 *
 * Slides up from the bottom of the phone shell with the tenant's logo +
 * name and an Install button. On iOS Safari (no `beforeinstallprompt`),
 * tapping the call-to-action expands an inline guide explaining the
 * Share → Add to Home Screen flow.
 *
 * Hidden when the app is already installed (standalone mode) or the user
 * has dismissed it for this tenant. See `useInstallPrompt` for the rules.
 */
// Screens with their own bottom action bar (Add to cart, Checkout, Place
// order). The banner is fixed to the bottom and sat on top of that bar, so a
// tap on the middle of "Add to cart" hit the banner instead (E2E L1-14).
const ACTION_BAR_ROUTE = /\/(item|cart|checkout|success|track|orders?)(\/|$)/;

export function shouldHideInstallBanner(pathname: string): boolean {
  return ACTION_BAR_ROUTE.test(pathname);
}

export function InstallBanner() {
  const { t } = useTranslation();
  const { branchSlug, branchConfig } = useBranch();
  const { canShow, platform, hasNativePrompt, promptInstall, dismiss } =
    useInstallPrompt(branchSlug);
  const [iosGuideOpen, setIosGuideOpen] = useState(false);
  const { pathname } = useLocation();

  if (!canShow || !branchConfig || shouldHideInstallBanner(pathname)) return null;

  const name = storefrontName(branchConfig, "this restaurant");
  const logoUrl =
    (branchConfig as { logoUrl?: string | null }).logoUrl ?? null;

  const handleInstall = async () => {
    if (platform === "ios") {
      setIosGuideOpen((v) => !v);
      return;
    }
    if (hasNativePrompt) {
      const outcome = await promptInstall();
      if (outcome === "dismissed") {
        // User declined the native prompt — don't keep nagging.
        dismiss();
      }
    }
  };

  return (
    <div
      role="dialog"
      aria-label={t("v2.install.aria_label", "Install app prompt")}
      className="pointer-events-auto fixed bottom-0 left-1/2 z-50 w-full max-w-[420px] -translate-x-1/2 px-3 pb-3"
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <div
        className="rounded-2xl bg-white shadow-[0_10px_40px_-12px_rgba(0,0,0,0.25)] ring-1 ring-black/5"
        style={{ borderTop: "3px solid var(--primary-500, #0baa57)" }}
      >
        <div className="flex items-center gap-3 p-3">
          <div
            className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl"
            style={{ background: "var(--primary-50, #e7f7ee)" }}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                className="h-full w-full object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <span
                className="text-lg font-bold"
                style={{ color: "var(--primary-700, #08793e)" }}
              >
                {name.slice(0, 1).toUpperCase()}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-gray-900">
              {t("v2.install.title", "Add {{name}} to your home screen", {
                name,
              })}
            </div>
            <div className="truncate text-xs text-gray-500">
              {platform === "ios"
                ? t(
                    "v2.install.subtitle_ios",
                    "Open it like an app — no app store needed.",
                  )
                : t(
                    "v2.install.subtitle",
                    "Faster checkout, works offline.",
                  )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleInstall}
            className="shrink-0 rounded-lg px-3 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 active:opacity-80"
            style={{ background: "var(--primary-500, #0baa57)" }}
          >
            {platform === "ios"
              ? t("v2.install.button_ios", "How to")
              : t("v2.install.button", "Install")}
          </button>
          <button
            type="button"
            aria-label={t("v2.install.dismiss", "Dismiss")}
            onClick={dismiss}
            className="-mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            {/* Plain × glyph — keeps zero icon-lib dependency. */}
            <span aria-hidden="true" className="text-lg leading-none">
              ×
            </span>
          </button>
        </div>
        {platform === "ios" && iosGuideOpen && (
          <div className="border-t border-gray-100 px-4 py-3 text-xs text-gray-700">
            <ol className="list-decimal space-y-1.5 ps-5">
              <li>
                {t(
                  "v2.install.ios_step_1",
                  "Tap the Share button in Safari's toolbar.",
                )}{" "}
                <span aria-hidden="true">⬆️</span>
              </li>
              <li>
                {t(
                  "v2.install.ios_step_2",
                  "Scroll and choose 'Add to Home Screen'.",
                )}
              </li>
              <li>
                {t(
                  "v2.install.ios_step_3",
                  "Tap Add — {{name}} appears on your home screen.",
                  { name },
                )}
              </li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}
